import { RefObject } from 'react';
import axios from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { MAX_FILE_SIZE, ProviderType, UploadProgressRef } from '../DocumentVault.meta';
import { useRemoveUploadFile } from '../hooks/useRemoveUploadFile';
import { queryKeys } from './queryKeys';
import { API } from '../../CommonUtils/axiosWithDefaults';

interface UseUploadFileMutationParams {
  uploadProgressRef?: RefObject<UploadProgressRef>;
  providerType?: ProviderType;
}

interface UploadFileParams {
  clientId: string;
  path: string;
  file: File;
  fileId?: string;
}

export const useInternalFileUploadMutation = ({
  uploadProgressRef,
  providerType,
}: UseUploadFileMutationParams = {}) => {
  const queryClient = useQueryClient();

  const removeUploadFile = useRemoveUploadFile();

  return useMutation(
    async ({ clientId, path, fileId, file }: UploadFileParams) => {
      if (file.size >= MAX_FILE_SIZE * 1e6) {
        if (fileId) {
          await removeUploadFile(fileId);
        }

        throw new Error(`Please upload a file up to ${MAX_FILE_SIZE}MB`);
      }

      const controller = new AbortController();

      try {
        if (uploadProgressRef?.current) {
          uploadProgressRef.current.bindOnCancel(controller);
        }

        const formData = new FormData();

        formData.append('file', file);

        await API.post(
          urlWithParams('/Consumer/DocumentVault/UploadFile', {
            clientId,
            path,
            providerType,
          }),
          formData,
          {
            signal: controller.signal,
            onUploadProgress: p => {
              if (uploadProgressRef?.current) {
                uploadProgressRef.current.onUploadProgress(p);
              }
            },
          },
        );

        if (uploadProgressRef?.current) {
          uploadProgressRef.current.onUploadSuccess();

          if (fileId) {
            await removeUploadFile(fileId);
          }
        }
      } catch (err) {
        if (uploadProgressRef?.current) {
          if (axios.isCancel(err)) {
            uploadProgressRef.current.onCancel();
          } else {
            uploadProgressRef.current.onError();
          }

          if (fileId) {
            await removeUploadFile(fileId);
          }
        }
        throw err;
      }
    },
    {
      onSettled(_, __, { clientId, path }) {
        queryClient.refetchQueries(
          queryKeys.listDocumentVaultEntities(clientId, encodeURIComponent(path)),
          { active: true },
        );
      },
    },
  );
};
